import { JSX } from "react";
import { STAVE_SPACING } from "../Chant";
import { getPitchY, LayoutNeume, Neume, NeumeNote } from "./EBChant";

const maybeHighlightNote = (
    neumeIndex: number,
    neumeNoteIndex: number,
    pitch: number,
    highlightedPitch: number | undefined,
    highlightedNote: [number, number] | undefined,
    id: string
) => {
    if (
        highlightedNote &&
        neumeIndex === highlightedNote[0] &&
        neumeNoteIndex === highlightedNote[1]
    ) {
        return `${id}-white`;
    } else {
        return id;
    }
};

export const getLayoutNeumes = (
    neumes: Neume[],
    isSmallScreen: boolean,
    measureText: (
        text: string,
        style: "normal" | "normal bold" | "drop cap"
    ) => number,
    highlightedNote: [number, number] | undefined
) => {
    const highlightedPitch = undefined;

    const layoutNeumesWithoutText: Omit<
        LayoutNeume,
        "textExtensionLeft" | "textExtensionRight" | "redDotOffset"
    >[] = neumes.map((neume, neumeIndex) => {
        let neumeExtensionLeft = 0;

        const svgNeume: JSX.Element[] = [];

        let leftOffset = 0;

        const addMora = (pitch: number, leftOffset: number) => {
            svgNeume.push(
                <use
                    xlinkHref="#mora"
                    x={leftOffset + 2}
                    y={getPitchY(pitch) - 5 - ((pitch % 2) - 1) * 1}
                ></use>
            );
        };

        const addNote = (
            note: NeumeNote,
            incrementLeft: boolean,
            renderMora: boolean,
            neumeNoteIndex: number
        ) => {
            if (note.isInclinatum) {
                svgNeume.push(
                    <use
                        xlinkHref={maybeHighlightNote(
                            neumeIndex,
                            neumeNoteIndex,
                            note.pitch,
                            highlightedPitch,
                            highlightedNote,
                            "#punctum-inclinatum"
                        )}
                        x={leftOffset}
                        y={getPitchY(note.pitch) - 4}
                    ></use>
                );

                if (incrementLeft) {
                    leftOffset += 4;
                }
            } else if (note.isQuilisma) {
                svgNeume.push(
                    <use
                        xlinkHref={maybeHighlightNote(
                            neumeIndex,
                            neumeNoteIndex,
                            note.pitch,
                            highlightedPitch,
                            highlightedNote,
                            "#quilisma"
                        )}
                        x={leftOffset}
                        y={getPitchY(note.pitch) - 4}
                    ></use>
                );

                if (incrementLeft) {
                    leftOffset += 6;
                }
            } else {
                svgNeume.push(
                    <use
                        xlinkHref={maybeHighlightNote(
                            neumeIndex,
                            neumeNoteIndex,
                            note.pitch,
                            highlightedPitch,
                            highlightedNote,
                            "#punctum-quadratum"
                        )}
                        x={leftOffset}
                        y={getPitchY(note.pitch) - 3.5}
                    ></use>
                );

                if (incrementLeft) {
                    leftOffset += 6;
                }
            }

            if (note.hasMora && renderMora) {
                addMora(note.pitch, leftOffset);

                leftOffset += 4;
            }
        };

        const addVerticalLine = (
            startPitch: number,
            endPitch: number,
            extraDescent: number,
            offset: number
        ) => {
            svgNeume.push(
                <line
                    x1={leftOffset + offset + 0.5}
                    x2={leftOffset + offset + 0.5}
                    y1={getPitchY(startPitch)}
                    y2={getPitchY(endPitch) + extraDescent}
                    stroke="black"
                    strokeWidth={1}
                />
            );
        };

        const addPodatus = (
            note1: NeumeNote,
            note2: NeumeNote,
            neumeNoteIndex: number
        ) => {
            if (note2.isLiquescent) {
                svgNeume.push(
                    <use
                        xlinkHref={maybeHighlightNote(
                            neumeIndex,
                            neumeNoteIndex,
                            note1.pitch,
                            highlightedPitch,
                            highlightedNote,
                            "#beginning-ascending-liquescent"
                        )}
                        x={leftOffset}
                        y={getPitchY(note1.pitch) - 4.5}
                    ></use>
                );
                leftOffset += 3;
                svgNeume.push(
                    <use
                        xlinkHref={maybeHighlightNote(
                            neumeIndex,
                            neumeNoteIndex + 1,
                            note2.pitch,
                            highlightedPitch,
                            highlightedNote,
                            "#terminating-ascending-liquescent"
                        )}
                        x={leftOffset}
                        y={getPitchY(note2.pitch) - 2.5}
                    ></use>
                );
                leftOffset += 3;
                addVerticalLine(note1.pitch, note2.pitch, 0, -1);
            } else {
                if (note1.isQuilisma) {
                    svgNeume.push(
                        <use
                            xlinkHref={maybeHighlightNote(
                                neumeIndex,
                                neumeNoteIndex,
                                note1.pitch,
                                highlightedPitch,
                                highlightedNote,
                                "#quilisma"
                            )}
                            x={leftOffset}
                            y={getPitchY(note1.pitch) - 4}
                        ></use>
                    );
                } else {
                    svgNeume.push(
                        <use
                            xlinkHref={maybeHighlightNote(
                                neumeIndex,
                                neumeNoteIndex,
                                note1.pitch,
                                highlightedPitch,
                                highlightedNote,
                                "#podatus-lower"
                            )}
                            x={leftOffset}
                            y={getPitchY(note1.pitch) - 2.5}
                        ></use>
                    );
                }
                svgNeume.push(
                    <use
                        xlinkHref={maybeHighlightNote(
                            neumeIndex,
                            neumeNoteIndex + 1,
                            note2.pitch,
                            highlightedPitch,
                            highlightedNote,
                            "#podatus-upper"
                        )}
                        x={leftOffset + 0.5}
                        y={getPitchY(note2.pitch) - 3.5}
                    ></use>
                );
                leftOffset += 6;
                addVerticalLine(note1.pitch, note2.pitch, 0, -1);
            }

            if (note1.hasMora) {
                addMora(note1.pitch, leftOffset);
            }

            if (note2.hasMora) {
                addMora(note2.pitch, leftOffset);
            }
        };

        const addPorrectus = (
            note1: NeumeNote,
            note2: NeumeNote,
            note3: NeumeNote,
            neumeNoteIndex: number
        ) => {
            const size = note1.pitch - note2.pitch;

            const leftWhite =
                highlightedNote &&
                neumeIndex === highlightedNote[0] &&
                neumeNoteIndex === highlightedNote[1];
            const rightWhite =
                highlightedNote &&
                neumeIndex === highlightedNote[0] &&
                neumeNoteIndex + 1 === highlightedNote[1];

            if (size === 1) {
                svgNeume.push(
                    <use
                        xlinkHref={
                            leftWhite
                                ? "#porrectus-1-left-white"
                                : rightWhite
                                ? "#porrectus-1-right-white"
                                : "#porrectus-1"
                        }
                        x={leftOffset}
                        y={getPitchY(note1.pitch) - 3.5}
                    ></use>
                );
            }
            if (size === 2) {
                svgNeume.push(
                    <use
                        xlinkHref={
                            leftWhite
                                ? "#porrectus-2-left-white"
                                : rightWhite
                                ? "#porrectus-2-right-white"
                                : "#porrectus-2"
                        }
                        x={leftOffset}
                        y={getPitchY(note1.pitch) - 3.5}
                    ></use>
                );
            }
            if (size === 3) {
                svgNeume.push(
                    <use
                        xlinkHref={
                            leftWhite
                                ? "#porrectus-3-left-white"
                                : rightWhite
                                ? "#porrectus-3-right-white"
                                : "#porrectus-3"
                        }
                        x={leftOffset}
                        y={getPitchY(note1.pitch) - 3.5}
                    ></use>
                );
            }
            leftOffset += 13;
            svgNeume.push(
                <use
                    xlinkHref={maybeHighlightNote(
                        neumeIndex,
                        neumeNoteIndex + 2,
                        note3.pitch,
                        highlightedPitch,
                        highlightedNote,
                        "#podatus-upper"
                    )}
                    x={leftOffset + 0.5}
                    y={getPitchY(note3.pitch) - 3.5}
                ></use>
            );
            leftOffset += 6;
            addVerticalLine(note2.pitch, note3.pitch, 0, -1);
        };

        if (neume.showFlat && neume.flatPitch) {
            svgNeume.push(
                <use
                    xlinkHref="#flat"
                    x={-10}
                    y={getPitchY(neume.flatPitch) - 11}
                ></use>
            );
            neumeExtensionLeft += 10;
        }

        let neumeNoteIndex = 0;

        let minNote = 0;
        let maxNote = 0;

        neume.elements.forEach((element) => {
            minNote = Math.min(minNote, ...element.notes.map((n) => n.pitch));
            maxNote = Math.max(maxNote, ...element.notes.map((n) => n.pitch));

            switch (element.type) {
                case "punctum":
                    addNote(element.notes[0], true, true, neumeNoteIndex);
                    break;

                case "podatus":
                    addPodatus(
                        element.notes[0],
                        element.notes[1],
                        neumeNoteIndex
                    );
                    break;

                case "clivis":
                    if (element.notes[1].isLiquescent) {
                        addVerticalLine(
                            element.notes[0].pitch,
                            element.notes[1].pitch,
                            element.notes[0].pitch - element.notes[1].pitch ===
                                1
                                ? 3.5 +
                                      ((STAVE_SPACING - 1) *
                                          (1 - (element.notes[1].pitch % 2))) /
                                          2
                                : 3.5,
                            0
                        );
                        addNote(element.notes[0], false, false, neumeNoteIndex);
                        leftOffset += 3;
                        svgNeume.push(
                            <use
                                xlinkHref={maybeHighlightNote(
                                    neumeIndex,
                                    neumeNoteIndex + 1,
                                    element.notes[1].pitch,
                                    highlightedPitch,
                                    highlightedNote,
                                    "#terminating-descending-liquescent"
                                )}
                                x={leftOffset}
                                y={getPitchY(element.notes[1].pitch) - 4.5}
                            ></use>
                        );
                        leftOffset += 3;
                        addVerticalLine(
                            element.notes[0].pitch,
                            element.notes[1].pitch,
                            0,
                            -1
                        );
                    } else {
                        addVerticalLine(
                            element.notes[0].pitch,
                            element.notes[1].pitch,
                            element.notes[0].pitch - element.notes[1].pitch ===
                                1
                                ? 3.5 +
                                      ((STAVE_SPACING - 1) *
                                          (1 - (element.notes[1].pitch % 2))) /
                                          2
                                : 3.5,
                            0
                        );
                        addNote(element.notes[0], true, false, neumeNoteIndex);
                        leftOffset -= 1;
                        addVerticalLine(
                            element.notes[0].pitch,
                            element.notes[1].pitch,
                            0,
                            0
                        );
                        addNote(
                            element.notes[1],
                            true,
                            false,
                            neumeNoteIndex + 1
                        );
                    }

                    if (element.notes[0].hasMora) {
                        addMora(element.notes[0].pitch, leftOffset - 4);
                    }

                    if (element.notes[1].hasMora) {
                        addMora(element.notes[1].pitch, leftOffset);
                    }

                    break;

                case "scandicus":
                    addNote(element.notes[0], true, true, neumeNoteIndex);
                    addPodatus(
                        element.notes[1],
                        element.notes[2],
                        neumeNoteIndex + 1
                    );
                    if (element.notes[1].hasMora) {
                        addMora(element.notes[0].pitch, leftOffset);
                    }

                    if (element.notes[2].hasMora) {
                        addMora(element.notes[1].pitch, leftOffset);
                    }
                    break;

                case "torculus":
                    addNote(element.notes[0], true, false, neumeNoteIndex);
                    leftOffset -= 1;
                    addVerticalLine(
                        element.notes[0].pitch,
                        element.notes[1].pitch,
                        0,
                        0
                    );
                    addNote(element.notes[1], true, false, neumeNoteIndex + 1);
                    leftOffset -= 1;
                    addVerticalLine(
                        element.notes[1].pitch,
                        element.notes[2].pitch,
                        0,
                        0
                    );
                    addNote(element.notes[2], true, true, neumeNoteIndex + 2);
                    break;

                case "porrectus":
                    addVerticalLine(
                        element.notes[0].pitch,
                        element.notes[1].pitch,
                        element.notes[0].pitch - element.notes[1].pitch === 1
                            ? 3.5 +
                                  ((STAVE_SPACING - 1) *
                                      (1 - (element.notes[1].pitch % 2))) /
                                      2
                            : 3.5,
                        0
                    );
                    addPorrectus(
                        element.notes[0],
                        element.notes[1],
                        element.notes[2],
                        neumeNoteIndex
                    );
                    break;

                case "pes subpunctis":
                    addPodatus(
                        element.notes[0],
                        element.notes[1],
                        neumeNoteIndex
                    );
                    leftOffset += 1;
                    element.notes.slice(2).forEach((note, noteIndex) => {
                        addNote(
                            note,
                            true,
                            true,
                            neumeNoteIndex + 2 + noteIndex
                        );
                    });
                    leftOffset += 2;
                    break;

                case "climacus":
                    addNote(element.notes[0], true, true, neumeNoteIndex);
                    addVerticalLine(
                        element.notes[0].pitch,
                        element.notes[1].pitch,
                        element.notes[0].pitch - element.notes[1].pitch === 1
                            ? 3.5 +
                                  ((STAVE_SPACING - 1) *
                                      (1 - (element.notes[1].pitch % 2))) /
                                      2
                            : 3.5,
                        -1
                    );
                    element.notes.slice(1).forEach((note, noteIndex) => {
                        addNote(
                            note,
                            true,
                            true,
                            neumeNoteIndex + 1 + noteIndex
                        );
                    });
                    leftOffset += 2;
                    break;

                case "quarter bar":
                    leftOffset += 2;
                    addVerticalLine(6, 8, 0, 0);
                    leftOffset += 2;
                    break;

                case "half bar":
                    leftOffset += 2;
                    addVerticalLine(2, 6, 0, 0);
                    leftOffset += 2;
                    break;

                case "full bar":
                    leftOffset += 2;
                    addVerticalLine(1, 7, 0, 0);
                    leftOffset += 2;
                    break;
            }

            if (!element.suppressSpace) {
                leftOffset += 2;
            }

            neumeNoteIndex += element.notes.length;
        });

        leftOffset += isSmallScreen ? 4 : 8;

        return {
            neume,
            svgNeume,
            neumeExtensionLeft,
            neumeExtensionRight: leftOffset,
            maxNote,
            minNote,
        };
    });

    const extensions = layoutNeumesWithoutText.map((layoutNeume) => {
        const fvi = Array.from(layoutNeume.neume.text).findIndex(
            (char, charIndex) => {
                if (charIndex !== 0 && (char === "y" || char === "Y")) {
                    return true;
                }

                if (
                    charIndex > 0 &&
                    char === "u" &&
                    Array.from(layoutNeume.neume.text)[charIndex - 1] === "q"
                ) {
                    return false;
                }

                return "aeiouAEIOUáéíóúÁÉÍÓÚæ".includes(char);
            }
        );
        const firstVowelIndex = fvi === -1 ? 0 : fvi;

        const textBeforeNeume = layoutNeume.neume.text.slice(
            0,
            firstVowelIndex
        );
        const textAfterNeume = layoutNeume.neume.text.slice(firstVowelIndex);

        const textExtensionLeft = measureText(
            textBeforeNeume,
            layoutNeume.neume.isBold ? "normal bold" : "normal"
        );
        const textExtensionRight = measureText(
            textAfterNeume,
            layoutNeume.neume.isBold ? "normal bold" : "normal"
        );

        return {
            textExtensionLeft,
            textExtensionRight,
            isEmpty: layoutNeume.neume.text.trim().length === 0,
        };
    });

    const bulletWidth = measureText("•", "normal");

    const layoutNeumes: LayoutNeume[] = layoutNeumesWithoutText.map(
        (layoutNeume, layoutNeumeIndex) => {
            let text = layoutNeume.neume.text;
            let textExtensionRight =
                extensions[layoutNeumeIndex].textExtensionRight;
            let neumeExtensionRight = layoutNeume.neumeExtensionRight;
            let redDotOffset: number | undefined = undefined;

            const endsWithSpace =
                layoutNeume.neume.text[layoutNeume.neume.text.length - 1] ===
                " ";

            const endsWithHyphen =
                layoutNeume.neume.text[layoutNeume.neume.text.length - 1] ===
                "-";

            if (layoutNeumeIndex < layoutNeumesWithoutText.length - 1) {
                const redDotFollows =
                    layoutNeumesWithoutText[layoutNeumeIndex + 1].neume
                        .hasRedDotBefore;

                if (redDotFollows) {
                    redDotOffset = textExtensionRight;

                    textExtensionRight += bulletWidth;
                }

                const spaceAvailableForNeume =
                    extensions[layoutNeumeIndex].textExtensionRight +
                    extensions[layoutNeumeIndex + 1].textExtensionLeft;

                const needsHyphen =
                    layoutNeume.neumeExtensionRight > spaceAvailableForNeume &&
                    layoutNeume.neume.text.trim().length > 0 &&
                    !extensions[layoutNeumeIndex + 1].isEmpty &&
                    !endsWithSpace &&
                    !endsWithHyphen;

                if (needsHyphen && !redDotFollows) {
                    text += "-";
                    textExtensionRight += measureText(
                        "-",
                        layoutNeume.neume.isBold ? "normal bold" : "normal"
                    );
                }
            }

            if (endsWithSpace) {
                textExtensionRight += 4;
            }

            return {
                ...layoutNeume,
                neume: { ...layoutNeume.neume, text },
                neumeExtensionRight,
                textExtensionLeft:
                    extensions[layoutNeumeIndex].textExtensionLeft,
                textExtensionRight: textExtensionRight,
                redDotOffset,
            };
        }
    );

    return layoutNeumes;
};
